<template>
  <nav
    class="fixed top-0 left-0 right-0 z-50 transition-all duration-500 transition-gpu"
    :class="[scrolled ? 'py-4' : 'py-6']"
  >
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="relative flex items-center justify-between h-14 px-4 rounded-2xl border transition-all duration-500 transition-gpu shadow-2xl"
        :class="[
          scrolled
            ? 'bg-premium-card/80 backdrop-blur-xl border-premium-border shadow-black/50'
            : 'bg-transparent border-transparent shadow-none',
        ]"
      >
        <!-- Logo -->
        <router-link
          :to="isLoggedIn ? '/dashboard' : '/'"
          class="flex items-center gap-2 group"
        >
          <div
            class="w-10 h-10 rounded-xl bg-premium-card border border-premium-border/50 flex items-center justify-center group-hover:border-brand-500/50 transition-colors"
          >
            <img
              src="@/assets/ideascope-new-logo-without-bg.png"
              alt="IdeaScope Logo"
              class="w-6 h-6 object-contain"
            />
          </div>
          <span
            class="text-lg font-bold tracking-tight text-premium-text group-hover:text-brand-400 transition-colors"
          >
            IdeaScope
          </span>
        </router-link>

        <!-- Desktop Navigation (centered) -->
        <div class="hidden md:flex items-center justify-center flex-1 gap-1">
          <router-link
            to="/features"
            class="px-3 py-2 text-sm font-medium text-premium-dim hover:text-premium-text transition-colors rounded-xl hover:bg-white/5"
          >
            Features
          </router-link>

          <router-link
            to="/pricing"
            class="px-3 py-2 text-sm font-medium text-premium-dim hover:text-premium-text transition-colors rounded-xl hover:bg-white/5"
          >
            Pricing
          </router-link>

          <!-- Signal Feed Link -->
          <router-link
            to="/signals"
            class="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors rounded-xl hover:bg-orange-500/10"
          >
            <Flame class="w-4 h-4" />
            Signals
          </router-link>

          <router-link
            to="/sample-reports"
            class="px-3 py-2 text-sm font-medium text-premium-dim hover:text-premium-text transition-colors rounded-xl hover:bg-white/5"
          >
            Sample Reports
          </router-link>
        </div>

        <!-- Auth Buttons -->
        <div class="hidden md:flex items-center gap-3">
          <div v-if="isLoggedIn" class="flex items-center gap-3">
            <!-- Notification Bell -->
            <div class="relative">
              <button
                @click="toggleNotifications"
                class="relative flex items-center justify-center w-8 h-8 rounded-xl bg-white/5 border border-premium-border/50 text-premium-dim hover:text-premium-text hover:border-brand-500/30 transition-all"
              >
                <Bell class="w-4 h-4" />
                <span
                  v-if="unreadCount > 0"
                  class="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-black text-white bg-brand-500 rounded-full shadow-lg shadow-brand-500/30"
                >
                  {{ unreadCount > 99 ? '99+' : unreadCount }}
                </span>
              </button>
              <NotificationPanel
                :visible="showNotifications"
                @close="showNotifications = false"
              />
            </div>

            <!-- User Avatar -->
            <div class="relative">
              <button
                @click="toggleDropdown"
                class="flex items-center justify-center w-8 h-8 rounded-full bg-brand-600 text-white text-xs font-bold ring-2 ring-premium-bg hover:scale-110 transition-all"
              >
                {{ userInitials }}
              </button>

              <!-- User Dropdown -->
              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
              >
                <div
                  v-show="showDropdown"
                  class="absolute right-0 mt-2 w-56 p-2 bg-premium-card border border-premium-border rounded-2xl shadow-2xl backdrop-blur-xl"
                >
                  <div class="px-3 py-2 border-b border-premium-border mb-2">
                    <div class="text-sm font-semibold text-premium-text line-clamp-1">
                      {{ this.$store.state.user?.name }}
                    </div>
                    <div class="text-xs text-premium-dim line-clamp-1">
                      {{ this.$store.state.user?.email }}
                    </div>
                  </div>
                  <button
                    @click="editProfile"
                    class="w-full flex items-center gap-3 p-2 text-sm text-premium-dim hover:text-premium-text hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <User class="w-4 h-4" />
                    Edit Profile
                  </button>
                  <router-link
                    to="/settings"
                    @click="showDropdown = false"
                    class="w-full flex items-center gap-3 p-2 text-sm text-premium-dim hover:text-premium-text hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <SettingsIcon class="w-4 h-4" />
                    Settings
                  </router-link>
                  <button
                    @click="logout"
                    class="w-full flex items-center gap-3 p-2 text-sm text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 rounded-lg transition-colors"
                  >
                    <LogOut class="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </transition>
            </div>
          </div>
          <template v-else>
            <router-link
              to="/login"
              class="px-4 py-2 text-sm font-medium text-premium-dim hover:text-premium-text transition-colors"
            >
              Login
            </router-link>
            <router-link
              to="/signup"
              class="px-5 py-2 text-sm font-semibold bg-brand-600 text-white rounded-xl hover:bg-brand-500 transition-all shadow-lg shadow-brand-600/20 active:scale-95"
            >
              Start Free
            </router-link>
          </template>
        </div>

        <!-- Mobile Menu Button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="md:hidden p-2 text-premium-dim hover:text-premium-text transition-colors"
        >
          <Menu v-if="!mobileMenuOpen" class="w-6 h-6" />
          <X v-else class="w-6 h-6" />
        </button>
      </div>
    </div>

    <!-- Mobile Menu Overlay -->
    <transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 translate-y-[-20px]"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-[-20px]"
    >
      <div
        v-if="mobileMenuOpen"
        class="md:hidden fixed inset-x-0 top-[72px] mx-4 p-6 bg-premium-card border border-premium-border rounded-[2rem] shadow-2xl z-40 overflow-y-auto max-h-[calc(100vh-100px)]"
      >
        <div class="flex flex-col gap-6">
          <!-- Mobile Links -->
          <div class="space-y-4">
            <p
              class="text-[10px] font-black uppercase tracking-[0.3em] text-premium-dim px-4"
            >
              Navigation
            </p>
            <div class="grid gap-2">
              <router-link
                v-for="link in navLinks"
                :key="link.to"
                :to="link.to"
                class="flex items-center gap-3 p-4 rounded-2xl hover:bg-white/5 transition-colors text-premium-text font-bold italic uppercase tracking-wider"
                @click="mobileMenuOpen = false"
              >
                <component
                  :is="getIconComponent(link.icon)"
                  class="w-5 h-5 text-brand-400"
                />
                {{ link.label }}
              </router-link>
            </div>
          </div>



          <!-- Auth Actions -->
          <div class="pt-6 border-t border-premium-border/50">
            <div v-if="isLoggedIn" class="grid gap-2">
              <div class="flex items-center gap-3 px-4 mb-4">
                <div
                  class="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-black italic"
                >
                  {{ userInitials }}
                </div>
                <div>
                  <div class="text-sm font-bold text-white">
                    {{ this.$store.state.user?.name }}
                  </div>
                  <div
                    class="text-[10px] text-premium-dim uppercase tracking-tight"
                  >
                    {{ this.$store.state.user?.email }}
                  </div>
                </div>
              </div>
              <router-link
                to="/profile/edit"
                class="flex items-center gap-3 p-4 rounded-2xl hover:bg-white/5 text-premium-dim font-bold uppercase tracking-widest text-xs"
                @click="mobileMenuOpen = false"
              >
                <User class="w-4 h-4" />
                Edit Profile
              </router-link>
              <button
                @click="
                  logout();
                  mobileMenuOpen = false;
                "
                class="flex items-center gap-3 p-4 rounded-2xl hover:bg-rose-500/10 text-rose-400 font-bold uppercase tracking-widest text-xs transition-colors"
              >
                <LogOut class="w-4 h-4" />
                Logout
              </button>
            </div>
            <div v-else class="grid gap-3">
              <router-link
                to="/login"
                class="w-full py-4 text-center text-premium-text font-bold uppercase tracking-widest text-sm rounded-2xl border border-premium-border"
                @click="mobileMenuOpen = false"
              >
                Login
              </router-link>
              <router-link
                to="/signup"
                class="w-full py-4 text-center bg-brand-600 text-white font-black uppercase tracking-widest text-sm rounded-2xl shadow-xl shadow-brand-600/20"
                @click="mobileMenuOpen = false"
              >
                Start Free
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </nav>
</template>

<script>
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Zap,
  Layers,
  FileText,
  BookOpen,
  HelpCircle,
  AlertTriangle,
  Flame,
  Target,
  DollarSign,
  Bell,
  Settings as SettingsIcon,
} from "lucide-vue-next";
import NotificationPanel from "./NotificationPanel.vue";

export default {
  components: {
    Menu,
    X,
    User,
    LogOut,
    ChevronDown,
    Zap,
    Layers,
    FileText,
    BookOpen,
    HelpCircle,
    AlertTriangle,
    Flame,
    Bell,
    SettingsIcon,
    NotificationPanel,
  },
  data() {
    return {
      mobileMenuOpen: false,
      showDropdown: false,
      showNotifications: false,
      dropdownTimeout: null,
      scrolled: false,
      pollInterval: null,
      navLinks: [
        { to: "/features", label: "Features", icon: "Target" },
        { to: "/pricing", label: "Pricing", icon: "DollarSign" },
        { to: "/signals", label: "Signals", icon: "Flame", highlight: true },
        { to: "/sample-reports", label: "Sample Reports", icon: "FileText" },
      ],
    };
  },
  computed: {
    userInitials() {
      const name = this.$store.state.user?.name || "U";
      return name.substring(0, 2).toUpperCase();
    },
    isLoggedIn() {
      return this.$store.state.isUserLoggedIn;
    },
    unreadCount() {
      return this.$store.state.unreadNotificationCount;
    },
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 20;
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
      this.showNotifications = false;
    },
    toggleNotifications() {
      this.showNotifications = !this.showNotifications;
      this.showDropdown = false;
    },
    handleClickOutside(e) {
      if (!this.$el.contains(e.target)) {
        this.showDropdown = false;
        this.showNotifications = false;
      }
    },
    startPolling() {
      if (this.isLoggedIn) {
        this.$store.dispatch("fetchUnreadCount");
        this.pollInterval = setInterval(() => {
          if (this.isLoggedIn) {
            this.$store.dispatch("fetchUnreadCount");
          }
        }, 60000);
      }
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    editProfile() {
      this.showDropdown = false;
      this.$router.push("/profile/edit");
    },
    logout() {
      this.stopPolling();
      this.$store.dispatch("logoutUser");
      this.$router.push("/");
    },
    getIconComponent(iconName) {
      const icons = {
        Target,
        DollarSign,
        Flame,
        FileText,
        BookOpen,
        HelpCircle,
        AlertTriangle,
      };
      return icons[iconName] || Target;
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    document.addEventListener("click", this.handleClickOutside);
    this.startPolling();
  },
  beforeUnmount() {
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("click", this.handleClickOutside);
    this.stopPolling();
    if (this.dropdownTimeout) clearTimeout(this.dropdownTimeout);
  },
};
</script>

<style scoped>
.transition-gpu {
  backface-visibility: hidden;
  transform: translateZ(0);
}
</style>
